// MARK SETTLED, in Astra's lighter look (UI rebuild batch 3). The logic is components/SettleButton.tsx's, copied, not
// re-derived: the button is not the settlement — money moving is — so the BFF refuses with 409 `legs_open` while a leg
// has neither moved nor netted; that refusal turns into ONE line naming the open legs and a deliberate second tap,
// "Mark settled anyway" (force: true — the audited override, which records NO ride), behind a native confirm that names
// the deal and the figure still open. Any other refusal takes the line down and is said through ServerLine; a
// `not_awaiting` (the sweep closed it meanwhile) pulls the lists in behind its sentence.
//
// LIGHTER, NOT WEAKER (ruling, 26 Oct 2026): the refusal is one line and two buttons. The panel's own Receive / Pay
// link is not repeated — the sheet this stands in carries the leg's door already. The part-paid pointer (short, or
// their margin? the party's Running account setting) and its exception sit behind one "Part-paid?" tap.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../../lib/errors';
import { api, refreshMoneyViews } from '../../../lib/api';
import { displayAsset, fmtAmount } from '../../../lib/format';
import { legWords } from '../../SettleButton';
import { ServerLine } from '../kit';
import type { Legs } from './parts';

/** The one refusal answered with the line: a leg is still open. (SettleButton.openLegs) */
function openLegs(e: unknown): Legs | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const body = e.body as { error?: string; legs?: Legs };
  return body.error === 'legs_open' && body.legs ? body.legs : null;
}

/** The ONE leg still open, if exactly one is. (SettleButton.openSide) */
function openSide(legs: Legs): { asset: string; open: number } | null {
  const take = !legs.take.settled, give = !legs.give.settled;
  if (take === give) return null;
  return take ? { asset: legs.take.asset, open: legs.take.open } : { asset: legs.give.asset, open: legs.give.open };
}

/** Money has moved on the open leg and some is still open — a part payment. (SettleButton.partial) */
function partial(legs: Legs): boolean {
  const side = openSide(legs);
  if (!side) return false;
  const leg = legs.take.settled ? legs.give : legs.take;
  return side.open > 0 && side.open < Number(leg.amount);
}

export function MarkSettled({ dealId, partySlug, onDone, quiet = false }: { dealId: string; partySlug: string; onDone?: () => void;
  /** On a register row (restored 1 Nov 2026): a quiet text link, not a bordered button — the same guarded override. */
  quiet?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<Legs | null>(null);
  const m = useMutation({
    mutationFn: (force: boolean) => api.settleDeal(dealId, { force }),
    onSuccess: () => { setOpen(null); refreshMoneyViews(qc); onDone?.(); },
    onError: (e) => {
      const legs = openLegs(e);
      if (legs) { setOpen(legs); return; }
      // Any other refusal takes the line down, so its sentence is the only thing left on screen.
      setOpen(null);
      if (e instanceof ApiError && e.code === 'not_awaiting') refreshMoneyViews(qc);
    },
  });
  const otherError = m.isError && !openLegs(m.error) ? m.error : null;

  // The override, asked the same way (its confirm names the figures); on a row, said in one quiet line that wraps.
  const markAnyway = (legs: Legs) => {
    const still = ([['take', legs.take], ['give', legs.give]] as const)
      .filter(([, l]) => !l.settled)
      .map(([side, l]) => `${displayAsset(l.asset)} ${fmtAmount(l.open, l.asset)} still to ${side === 'take' ? 'receive' : 'pay'}`)
      .join(' and ');
    if (confirm(`Mark ${dealId} settled with ${still}? This records no ride — the figure is written off, not carried on their account.`)) m.mutate(true);
  };
  if (open && quiet) {
    return (
      <span className="flex flex-wrap justify-end gap-x-3 text-body">
        <span className="text-warn">Legs open</span>
        <button type="button" className="tap-44 text-neg hover:underline" disabled={m.isPending} onClick={() => markAnyway(open)}>{m.isPending ? 'Marking…' : 'Mark anyway'}</button>
        <button type="button" className="tap-44 text-tg-hint hover:text-tg-text" onClick={() => setOpen(null)}>Keep open</button>
      </span>
    );
  }
  if (open) {
    return (
      <div className="space-y-3 rounded-lg bg-warn-bg p-3">
        <p className="text-body text-warn">Not settled — {legWords(open.take, 'take')} · {legWords(open.give, 'give')}.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary px-3 text-body" onClick={() => setOpen(null)}>Keep open</button>
          <button type="button" className="btn-secondary px-3 text-body text-neg" disabled={m.isPending} onClick={() => markAnyway(open)}>
            {m.isPending ? 'Marking…' : 'Mark settled anyway'}
          </button>
        </div>
        {partial(open) && (
          <details className="text-meta text-tg-hint">
            <summary className="cursor-pointer max-lg:py-2">Part-paid?</summary>
            <p className="mt-1">
              Short, or their margin? Set Running account on{' '}
              <Link to={`/parties/${encodeURIComponent(partySlug)}?entity=dubaiotc`} className="btn-link text-meta">their party page</Link>.
            </p>
            <p>Running account never closes a held or reopened trade.</p>
          </details>
        )}
        {otherError != null && <ServerLine error={otherError} />}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {/* The row's actions are ONE kit button style at text-body (Astra's audit P9, 1 Nov 2026): Mark settled was a meta-size line
          under the coloured doors. `quiet` keeps it secondary, never smaller. */}
      <button type="button" className={quiet ? 'btn-secondary min-h-11 whitespace-nowrap px-3 text-body text-tg-hint disabled:opacity-50 lg:min-h-10' : 'btn-secondary px-3 text-body text-tg-hint'} disabled={m.isPending} onClick={() => m.mutate(false)}>
        {m.isPending ? 'Settling…' : 'Mark settled'}
      </button>
      {otherError != null && <ServerLine error={otherError} />}
    </div>
  );
}
